import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import client from '../../../shared/api/client';
import { variantUrl } from '../../../shared/lib/images';

const fmt = d =>
  d ? new Date(d).toLocaleString('es-PE', { dateStyle: 'medium', timeStyle: 'short' }) : '';

/** Seguimiento público de un pedido (sin iniciar sesión). El enlace lleva un token no adivinable. */
const TrackingPage = () => {
  const { token } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['tracking', token],
    queryFn: () => client.get(`/tracking/${token}`).then(r => r.data),
    retry: false,
    refetchInterval: 60000,
  });

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-brand-muted">Cargando…</div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="text-brand-text">No encontramos este pedido.</p>
        <p className="text-sm text-brand-muted">Revisa que el enlace esté completo.</p>
        <Link to="/" className="text-sm text-brand-accent underline">Ir a la tienda</Link>
      </div>
    );
  }

  const cancelled = data.state === 'cancelled';
  const current = data.steps.findIndex(s => s.state === data.state);
  const doneAt = Object.fromEntries(data.timeline.map(e => [e.state, e.at]));

  return (
    <div className="min-h-screen px-4 py-10">
      <div className="mx-auto w-full max-w-xl">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand-faint">Seguimiento de pedido</p>
        <h1 className="mt-1 text-2xl font-semibold text-brand-text">{data.reference}</h1>
        {data.customer_first_name && (
          <p className="mt-1 text-sm text-brand-muted">Hola {data.customer_first_name}, así va tu pedido.</p>
        )}

        <div className={`mt-6 rounded border px-4 py-3 ${cancelled ? 'border-brand-red/40' : 'border-brand-accent/40'}`}>
          <p className={`text-base font-medium ${cancelled ? 'text-brand-red' : 'text-brand-accent'}`}>
            {data.state_label}
          </p>
          {data.scheduled_at && data.delivery_type === 'pickup' && (
            <p className="mt-1 text-sm text-brand-muted">Recojo: {fmt(data.scheduled_at)}</p>
          )}
        </div>

        {!cancelled && (
          <ol className="mt-6 space-y-4">
            {data.steps.map((s, i) => {
              const done = i <= current;
              return (
                <li key={s.state} className="flex items-start gap-3">
                  <span
                    className={`mt-1 h-3 w-3 shrink-0 rounded-full border ${
                      done ? 'bg-brand-accent border-brand-accent' : 'border-brand-border-md'
                    }`}
                  />
                  <div>
                    <p className={`text-sm ${done ? 'text-brand-text' : 'text-brand-faint'}`}>{s.label}</p>
                    {doneAt[s.state] && <p className="text-xs text-brand-muted">{fmt(doneAt[s.state])}</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {data.shipping && (
          <div className="mt-6 rounded border border-brand-border-md px-4 py-3 text-sm">
            <p className="font-medium text-brand-text">Envío{data.shipping.carrier ? ` con ${data.shipping.carrier}` : ''}</p>
            {data.shipping.code && <p className="mt-1 text-brand-muted">Código: <span className="font-mono">{data.shipping.code}</span></p>}
            {data.shipping.url && (
              <a href={data.shipping.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-brand-accent underline">
                Rastrear en el transportista
              </a>
            )}
          </div>
        )}

        <h2 className="mt-8 font-mono text-[10px] uppercase tracking-[0.18em] text-brand-faint">Productos</h2>
        <ul className="mt-3 divide-y divide-brand-border-md">
          {data.items.map((it, i) => (
            <li key={i} className="flex items-center gap-3 py-3">
              {it.img && (
                <img src={variantUrl(it.img, 400)} alt="" decoding="async" loading="lazy"
                     className="h-14 w-14 rounded object-cover bg-white" />
              )}
              <span className="flex-1 text-sm text-brand-text">{it.name}</span>
              <span className="text-sm text-brand-muted">x{it.qty}</span>
            </li>
          ))}
        </ul>

        <p className="mt-8 text-xs text-brand-faint">¿Dudas? Escríbenos por WhatsApp y menciona {data.reference}.</p>
      </div>
    </div>
  );
};

export default TrackingPage;
